import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ProductType } from "@/core";
import { formatPrice } from "@/lib";
import {
  calculateDynamicRate,
  calculateTotalPayout,
} from "@/lib/miles-calculations";
import { TransactionTypes, useDashboardStore } from "@/stores";
import { HourglassIcon } from "lucide-react";
import { useState } from "react";

interface InactivityDialogProps {
  onSecureQuote?: () => void;
  onEmailDetails?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const InactivityDialog = ({
  onEmailDetails,
  onSecureQuote,
  open: externalOpen,
  onOpenChange,
}: InactivityDialogProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const program = useDashboardStore((state) => state.program);

  // Use external open state if provided, otherwise use internal state
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const handleClose = () => {
    setShowDetails(false);
    setOpen(false);
  };

  const handleEmailDetails = () => {
    handleClose();
    onEmailDetails?.();
  };

  const handleSecureQuote = () => {
    handleClose();
    onSecureQuote?.();
  };

  if (!program) {
    return null;
  }
  const transactionType =
    program.buyOrSell === ProductType.BUY
      ? TransactionTypes.BUY
      : TransactionTypes.SELL;

  const rate = calculateDynamicRate({
    miles: program.sanitizedMiles,
    transactionType,
    sellPrices: program.sellPrices,
    buyPrices: program.buyPrices,
    fallbackRate: 1,
  });

  const { statusBonusAmount, promoCodeDiscount } = calculateTotalPayout({
    totalMiles: program.sanitizedMiles,
    dynamicRate: rate,
    loyaltyStatus: program.loyaltyStatus,
    transactionType,
    promoCode: program.promoCode,
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        if (!newOpen) setShowDetails(false);

        setOpen(newOpen);
      }}
    >
      <DialogContent className="sm:max-w-md rounded-sm max-h-screen overflow-auto">
        {!showDetails ? (
          <div className="flex flex-col gap-6">
            <DialogTitle className="text-xl font-semibold flex gap-2 items-center">
              <div className="bg-black w-fit p-2 rounded-sm bg-white">
                <HourglassIcon className="h-full w-full" />
              </div>
              Still thinking it over?
            </DialogTitle>
            <p className="text-muted-foreground">
              Ready to lock in your payout now? Or prefer to get a copy of the
              quote and return later?
            </p>
            <div className="flex flex-col gap-4">
              <Button onClick={handleSecureQuote} className="w-full rounded-sm">
                Yes, I'm ready - secure my quote
              </Button>
              <Button
                onClick={() => setShowDetails(true)}
                variant="secondary"
                className="w-full rounded-sm"
              >
                Email me the details
              </Button>
            </div>
          </div>
        ) : (
          <>
            <DialogHeader className="text-left space-y-3">
              <DialogTitle className="text-xl font-semibold flex gap-2 items-center">
                <div className="bg-accent p-2 rounded-sm">
                  <img
                    src="/dashboard/emailPopup/shield.svg"
                    alt="shield"
                    className="h-full w-full"
                  />
                </div>
                Your Quote Details
              </DialogTitle>
            </DialogHeader>

            <div className="flex flex-col gap-4 mt-4">
              <div className="p-4 bg-slate-200/30 rounded-sm">
                <div className="w-full flex gap-1">
                  <span>Airline:</span>

                  <div>{program.airline}</div>
                </div>
                <p>
                  Rate:{" "}
                  {formatPrice({
                    price: rate * 100,
                    fractionDigits: 2,
                  })}
                  /100 Miles
                </p>
                {program.loyaltyStatus !== null && statusBonusAmount > 0 && (
                  <div className="flex justify-between items-center">
                    <p className="font-medium ">
                      Loyalty Bonus{" "}
                      <span className="text-blue-600 font-semibold">
                        {program.loyaltyStatus?.name}
                      </span>
                    </p>
                    <span className="font-semibold text-blue-600">
                      {formatPrice({
                        price: statusBonusAmount,
                      })}
                    </span>
                  </div>
                )}
                {program.promoCode && promoCodeDiscount !== 0 && (
                  <div className="flex justify-between items-center">
                    <p className="font-medium ">
                      Promo Code{" "}
                      <span className="text-blue-600 font-semibold">
                        {program.promoCode.name}
                      </span>
                    </p>
                    <span className={`font-semibold text-blue-600`}>
                      {formatPrice({
                        price: promoCodeDiscount,
                      })}
                    </span>
                  </div>
                )}
                <p>Quantity: {program.sanitizedMiles.toLocaleString()} miles</p>
                <p>
                  Program Total:{" "}
                  {formatPrice({
                    price: program.totalPayout,
                  })}
                </p>
              </div>

              <div className="p-4 rounded text-right font-semibold">
                Total Payout:{" "}
                {formatPrice({
                  price: program.totalPayout,
                })}
              </div>
              <Button
                onClick={handleEmailDetails}
                className="text-white font-medium py-3"
                size="lg"
              >
                Email Me This Offer
              </Button>
              <Button
                onClick={handleClose}
                variant="link"
                className="font-medium"
              >
                Adjust the Quote
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
